import {
  type CollectionReference,
  type DocumentData,
  type DocumentSnapshot,
  type Query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
  Timestamp,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

import { callable } from '@/lib/firebase/callable'
import { createReasonGuard, parseCallableError } from '@/lib/firebase/errors'
import { getFirebaseFirestore, getFirebaseFunctions } from '@/lib/firebase/app'
import { type Subscription, subscribeCollection, subscribeDoc } from '@/lib/firebase/subscribe'
import {
  type AgentCopyParams,
  type AgentMessageStep,
  type AgentOutcome,
  type AgentSource,
  type AgentStreamChunk,
  type AgentThreadStatus,
  type ChatMessage,
  type MessageContext,
  type MessageContextItem,
  type MessageContextItemKind,
  type ChatMessageAttachment,
  type ChatThread,
  type DeleteThreadInput,
  type DeleteThreadResult,
  type PendingAction,
  type PendingActionStatus,
  type PendingConnect,
  type PendingConnectStatus,
  type PendingQuestion,
  type PendingQuestionStatus,
  type SendMessageInput,
  type SendMessageResult,
  type ServerMessageRole,
  type ToolKind,
  toMessageRole,
} from '@/types/chat'
import type { SocialPlatform } from '@/types/connections'

export function threadPath(
  workspaceId: string,
  brandId: string,
): CollectionReference<DocumentData> {
  return collection(
    getFirebaseFirestore(),
    'workspaces',
    workspaceId,
    'brands',
    brandId,
    'agentThreads',
  )
}

export function messagesPath(
  workspaceId: string,
  brandId: string,
  threadId: string,
): CollectionReference<DocumentData> {
  return collection(threadPath(workspaceId, brandId), threadId, 'messages')
}

/** `null` when the server has not stamped this instant yet; reads ISO strings and Firestore Timestamps. */
function toIsoStringOrNull(value: unknown): string | null {
  if (value instanceof Timestamp) return value.toDate().toISOString()
  if (typeof value !== 'string') return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
}

const SERVER_MESSAGE_ROLES: readonly ServerMessageRole[] = ['user', 'model', 'system']

function asServerRole(value: unknown): ServerMessageRole {
  return typeof value === 'string' && (SERVER_MESSAGE_ROLES as readonly string[]).includes(value)
    ? (value as ServerMessageRole)
    : 'system'
}

const AGENT_THREAD_STATUSES: readonly AgentThreadStatus[] = ['active', 'closed']

function asThreadStatus(value: unknown): AgentThreadStatus {
  return typeof value === 'string' && (AGENT_THREAD_STATUSES as readonly string[]).includes(value)
    ? (value as AgentThreadStatus)
    : 'active'
}

function toChatThread(snapshot: QueryDocumentSnapshot<DocumentData>): ChatThread {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    type: 'chat',
    status: asThreadStatus(data.status),
    title: String(data.title ?? ''),
    messageCount: Number(data.messageCount ?? 0),
    lastMessageAt: toIsoStringOrNull(data.lastMessageAt),
    createdAt: toIsoStringOrNull(data.createdAt),
  }
}

const TOOL_KINDS: readonly ToolKind[] = [
  'thinking',
  'web-search',
  'research',
  'drafting',
  'editing',
  'generating',
  'navigating',
]

function isToolKind(value: unknown): value is ToolKind {
  return typeof value === 'string' && (TOOL_KINDS as readonly string[]).includes(value)
}

function toAgentSource(value: unknown): AgentSource | null {
  if (typeof value !== 'object' || value === null) return null
  const raw = value as Record<string, unknown>
  if (typeof raw.ref !== 'string' || (raw.kind !== 'web' && raw.kind !== 'knowledge')) return null
  return {
    kind: raw.kind,
    ref: raw.ref,
    ...(typeof raw.title === 'string' && raw.title ? { title: raw.title } : {}),
    ...(typeof raw.favicon === 'string' && raw.favicon ? { favicon: raw.favicon } : {}),
  }
}

function toAgentSources(value: unknown): AgentSource[] | undefined {
  if (!Array.isArray(value)) return undefined
  const sources = value
    .map(toAgentSource)
    .filter((source): source is AgentSource => source !== null)
  return sources.length > 0 ? sources : undefined
}

function toAgentCopyParams(value: unknown): AgentCopyParams | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  return value as AgentCopyParams
}

function toAgentOutcome(value: unknown): AgentOutcome | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const raw = value as Record<string, unknown>
  if (typeof raw.key !== 'string') return undefined
  return {
    key: raw.key as AgentOutcome['key'],
    ...(toAgentCopyParams(raw.params) ? { params: toAgentCopyParams(raw.params) } : {}),
  }
}

function toAgentMessageStep(value: unknown): AgentMessageStep | null {
  if (typeof value !== 'object' || value === null) return null
  const raw = value as Record<string, unknown>
  if (
    typeof raw.id !== 'string' ||
    typeof raw.labelKey !== 'string' ||
    typeof raw.ok !== 'boolean' ||
    typeof raw.startedAt !== 'string' ||
    !isToolKind(raw.kind)
  ) {
    return null
  }
  return {
    id: raw.id,
    tool: typeof raw.tool === 'string' ? raw.tool : undefined,
    kind: raw.kind,
    labelKey: raw.labelKey as AgentMessageStep['labelKey'],
    ...(toAgentCopyParams(raw.labelParams)
      ? { labelParams: toAgentCopyParams(raw.labelParams) }
      : {}),
    startedAt: raw.startedAt,
    completedAt: typeof raw.completedAt === 'string' ? raw.completedAt : undefined,
    ok: raw.ok,
    sources: toAgentSources(raw.sources),
    ...(toAgentOutcome(raw.outcome) ? { outcome: toAgentOutcome(raw.outcome) } : {}),
  }
}

function toAgentMessageSteps(value: unknown): AgentMessageStep[] | undefined {
  if (!Array.isArray(value)) return undefined
  const steps = value
    .map(toAgentMessageStep)
    .filter((step): step is AgentMessageStep => step !== null)
  return steps.length > 0 ? steps : undefined
}

function toChatMessageAttachments(value: unknown): ChatMessageAttachment[] | undefined {
  if (!Array.isArray(value)) return undefined
  const attachments = value.flatMap((entry) =>
    typeof entry?.itemId === 'string' ? [{ itemId: entry.itemId as string }] : [],
  )
  return attachments.length > 0 ? attachments : undefined
}

const MESSAGE_CONTEXT_ITEM_KINDS: readonly MessageContextItemKind[] = [
  'post',
  'document',
  'insight',
  'design',
]

function toMessageContext(value: unknown): MessageContext | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const raw = value as { screen?: unknown; item?: unknown }
  const screen = typeof raw.screen === 'string' && raw.screen !== '' ? raw.screen : undefined
  const rawItem = raw.item as { kind?: unknown; ref?: unknown; excerpt?: unknown } | undefined
  const kind = MESSAGE_CONTEXT_ITEM_KINDS.find((candidate) => candidate === rawItem?.kind)
  const item: MessageContextItem | undefined =
    kind && typeof rawItem?.ref === 'string' && rawItem.ref !== ''
      ? {
          kind,
          ref: rawItem.ref,
          ...(typeof rawItem.excerpt === 'string' && rawItem.excerpt !== ''
            ? { excerpt: rawItem.excerpt }
            : {}),
        }
      : undefined
  if (!screen && !item) return undefined
  return { ...(screen ? { screen } : {}), ...(item ? { item } : {}) }
}

function toChatMessage(
  threadId: string,
  snapshot: QueryDocumentSnapshot<DocumentData>,
): ChatMessage {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    threadId,
    role: toMessageRole(asServerRole(data.role)),
    text: String(data.text ?? ''),
    index: Number(data.index ?? 0),
    createdAt: toIsoStringOrNull(data.createdAt),
    steps: toAgentMessageSteps(data.steps),
    attachments: toChatMessageAttachments(data.attachments),
    context: toMessageContext(data.context),
  }
}

function toChatThreadDoc(snapshot: DocumentSnapshot<DocumentData>): ChatThread | undefined {
  return snapshot.exists() ? toChatThread(snapshot) : undefined
}

export interface SubscribeThreadInput {
  workspaceId: string
  brandId: string
  threadId: string
}

export function subscribeThread(input: SubscribeThreadInput): Subscription<ChatThread> {
  return subscribeDoc(
    doc(threadPath(input.workspaceId, input.brandId), input.threadId),
    toChatThreadDoc,
  )
}

export interface SubscribeThreadsInput {
  workspaceId: string
  brandId: string
  uid: string
  pageSize: number
}

export interface ThreadPageCursor {
  readonly doc: QueryDocumentSnapshot<DocumentData>
}

export interface ThreadPage {
  threads: ChatThread[]
  cursor: ThreadPageCursor | null
}

function threadsQuery(
  input: SubscribeThreadsInput,
  ...extra: QueryConstraint[]
): Query<DocumentData> {
  return query(
    threadPath(input.workspaceId, input.brandId),
    where('createdByUserId', '==', input.uid),
    orderBy('lastMessageAt', 'desc'),
    ...extra,
    limit(input.pageSize),
  )
}

function toCursor(
  snapshot: QuerySnapshot<DocumentData>,
  pageSize: number,
): ThreadPageCursor | null {
  const last = snapshot.docs.length === pageSize ? snapshot.docs.at(-1) : undefined
  return last ? { doc: last } : null
}

function toThreadPage(snapshot: QuerySnapshot<DocumentData>, pageSize: number): ThreadPage {
  return {
    threads: snapshot.docs.map(toChatThread),
    cursor: toCursor(snapshot, pageSize),
  }
}

export interface ThreadListSubscription extends Subscription<ChatThread[]> {
  /** Position after the live page, `null` while that page is not full. */
  cursor: () => ThreadPageCursor | null
}

/** Live first page of the user's open threads of one type, newest first; the cursor is a function, not subscription state. */
export function subscribeThreads(input: SubscribeThreadsInput): ThreadListSubscription {
  let cursor: ThreadPageCursor | null = null

  const sub = subscribeCollection(threadsQuery(input), (snapshot) => {
    cursor = toCursor(snapshot, input.pageSize)
    return snapshot.docs.map(toChatThread)
  })

  return { ...sub, cursor: () => cursor }
}

export interface ReadThreadPageInput extends SubscribeThreadsInput {
  cursor?: ThreadPageCursor | null
}

export async function readThreadPage(input: ReadThreadPageInput): Promise<ThreadPage> {
  const snapshot = await getDocs(
    input.cursor ? threadsQuery(input, startAfter(input.cursor.doc)) : threadsQuery(input),
  )
  return toThreadPage(snapshot, input.pageSize)
}

export function pendingActionsPath(
  workspaceId: string,
  brandId: string,
  threadId: string,
): CollectionReference<DocumentData> {
  return collection(threadPath(workspaceId, brandId), threadId, 'pendingActions')
}

function asPendingActionStatus(value: unknown): PendingActionStatus {
  return value === 'executed' || value === 'declined' || value === 'expired' ? value : 'pending'
}

function toFailureReason(value: unknown): string | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const result = value as { ok?: unknown; error?: unknown }
  if (result.ok !== false || typeof result.error !== 'string') return undefined
  return result.error.trim() || undefined
}

function toPendingAction(snapshot: QueryDocumentSnapshot<DocumentData>): PendingAction {
  const data = snapshot.data()
  const undo = data.undo as { undoable?: unknown; note?: unknown } | undefined
  const failureReason = toFailureReason(data.result)

  return {
    id: snapshot.id,
    toolName: String(data.toolName ?? ''),
    summary: String(data.summary ?? ''),
    input:
      typeof data.input === 'object' && data.input !== null
        ? (data.input as Record<string, unknown>)
        : {},
    status: asPendingActionStatus(data.status),
    createdAt: toIsoStringOrNull(data.createdAt),
    expiresAt: toIsoStringOrNull(data.expiresAt),
    ...(failureReason ? { failureReason } : {}),
    ...(typeof undo?.undoable === 'boolean' ? { undoable: undo.undoable } : {}),
    ...(typeof undo?.note === 'string' ? { undoNote: undo.note } : {}),
  }
}

export interface SubscribePendingActionsInput {
  workspaceId: string
  brandId: string
  threadId: string
}

/** Live pending actions for one thread; a subscription so it survives a reload. */
export function subscribePendingActions(
  input: SubscribePendingActionsInput,
): Subscription<PendingAction[]> {
  return subscribeCollection(
    query(
      pendingActionsPath(input.workspaceId, input.brandId, input.threadId),
      orderBy('createdAt', 'desc'),
    ),
    (snapshot) => snapshot.docs.map(toPendingAction),
  )
}

export function pendingQuestionsPath(
  workspaceId: string,
  brandId: string,
  threadId: string,
): CollectionReference<DocumentData> {
  return collection(threadPath(workspaceId, brandId), threadId, 'pendingQuestions')
}

function asPendingQuestionStatus(value: unknown): PendingQuestionStatus {
  return value === 'answered' || value === 'expired' ? value : 'pending'
}

function toPendingQuestion(snapshot: QueryDocumentSnapshot<DocumentData>): PendingQuestion {
  const data = snapshot.data()
  const options = Array.isArray(data.options) ? data.options.map(String) : []
  const resolvedAt = 'resolvedAt' in data ? toIsoStringOrNull(data.resolvedAt) : undefined

  return {
    id: snapshot.id,
    question: String(data.question ?? ''),
    options,
    allowFreeText: Boolean(data.allowFreeText),
    status: asPendingQuestionStatus(data.status),
    requestedByUserId: String(data.requestedByUserId ?? ''),
    createdAt: toIsoStringOrNull(data.createdAt),
    expiresAt: toIsoStringOrNull(data.expiresAt),
    ...(resolvedAt !== undefined ? { resolvedAt } : {}),
    ...(typeof data.answer === 'string' ? { answer: data.answer } : {}),
  }
}

export interface SubscribePendingQuestionsInput {
  workspaceId: string
  brandId: string
  threadId: string
}

/** Live pending questions for one thread; mirrors `subscribePendingActions`. */
export function subscribePendingQuestions(
  input: SubscribePendingQuestionsInput,
): Subscription<PendingQuestion[]> {
  return subscribeCollection(
    query(
      pendingQuestionsPath(input.workspaceId, input.brandId, input.threadId),
      orderBy('createdAt', 'desc'),
    ),
    (snapshot) => snapshot.docs.map(toPendingQuestion),
  )
}

export function pendingConnectsPath(
  workspaceId: string,
  brandId: string,
  threadId: string,
): CollectionReference<DocumentData> {
  return collection(threadPath(workspaceId, brandId), threadId, 'pendingConnects')
}

const SOCIAL_PLATFORMS: readonly SocialPlatform[] = ['linkedin', 'whatsapp']

function asSocialPlatform(value: unknown): SocialPlatform {
  return typeof value === 'string' && (SOCIAL_PLATFORMS as readonly string[]).includes(value)
    ? (value as SocialPlatform)
    : 'linkedin'
}

function asPendingConnectStatus(value: unknown): PendingConnectStatus {
  return value === 'completed' || value === 'expired' ? value : 'pending'
}

function toPendingConnect(snapshot: QueryDocumentSnapshot<DocumentData>): PendingConnect {
  const data = snapshot.data()
  const resolvedAt = 'resolvedAt' in data ? toIsoStringOrNull(data.resolvedAt) : undefined

  return {
    id: snapshot.id,
    platform: asSocialPlatform(data.platform),
    status: asPendingConnectStatus(data.status),
    requestedByUserId: String(data.requestedByUserId ?? ''),
    createdAt: toIsoStringOrNull(data.createdAt),
    expiresAt: toIsoStringOrNull(data.expiresAt),
    ...(resolvedAt !== undefined ? { resolvedAt } : {}),
  }
}

export interface SubscribePendingConnectsInput {
  workspaceId: string
  brandId: string
  threadId: string
}

/** Live pending social-connect requests for one thread; read-only, completion is decided server-side. */
export function subscribePendingConnects(
  input: SubscribePendingConnectsInput,
): Subscription<PendingConnect[]> {
  return subscribeCollection(
    query(
      pendingConnectsPath(input.workspaceId, input.brandId, input.threadId),
      orderBy('createdAt', 'desc'),
    ),
    (snapshot) => snapshot.docs.map(toPendingConnect),
  )
}

export interface SubscribeMessagesInput {
  workspaceId: string
  brandId: string
  threadId: string
}

export function subscribeMessages(input: SubscribeMessagesInput): Subscription<ChatMessage[]> {
  return subscribeCollection(
    query(messagesPath(input.workspaceId, input.brandId, input.threadId), orderBy('index', 'asc')),
    (snapshot) => snapshot.docs.map((doc) => toChatMessage(input.threadId, doc)),
  )
}

const CHAT_SEND_REASONS = ['rate-limited'] as const

export const isChatRateLimitedReason = createReasonGuard(CHAT_SEND_REASONS)

export const sendAgentMessage = callable<SendMessageInput, SendMessageResult>('sendAgentMessage', {
  timeout: 550_000,
})

const THREAD_DELETE_REASONS = ['agent_thread_owner_mismatch'] as const

export const isThreadOwnerMismatchReason = createReasonGuard(THREAD_DELETE_REASONS)

export const deleteAgentThread = callable<DeleteThreadInput, DeleteThreadResult>(
  'deleteAgentThread',
)

export class ChatStreamAbortedError extends Error {
  constructor() {
    super('The chat stream was aborted.')
    this.name = 'ChatStreamAbortedError'
  }
}

export function isChatStreamAbortedError(error: unknown): error is ChatStreamAbortedError {
  return error instanceof ChatStreamAbortedError
}

function isAbortSignalError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  )
}

function supportsCallableStreaming(): boolean {
  return typeof ReadableStream !== 'undefined' && typeof fetch !== 'undefined'
}

export interface StreamAgentMessageOptions {
  signal?: AbortSignal
  onChunk: (chunk: AgentStreamChunk) => void
}

export async function streamAgentMessage(
  input: SendMessageInput,
  options: StreamAgentMessageOptions,
): Promise<SendMessageResult> {
  if (!supportsCallableStreaming()) {
    if (options.signal?.aborted) {
      throw new ChatStreamAbortedError()
    }
    try {
      const result = await sendAgentMessage(input)
      if (options.signal?.aborted) {
        throw new ChatStreamAbortedError()
      }
      return result
    } catch (cause) {
      if (options.signal?.aborted || isAbortSignalError(cause)) {
        throw new ChatStreamAbortedError()
      }
      throw cause
    }
  }

  try {
    const fn = httpsCallable<SendMessageInput, SendMessageResult, AgentStreamChunk>(
      getFirebaseFunctions(),
      'sendAgentMessage',
    )
    const { stream, data } = await fn.stream(input, { signal: options.signal })

    for await (const chunk of stream) {
      options.onChunk(chunk)
    }

    return await data
  } catch (cause) {
    if (options.signal?.aborted || isAbortSignalError(cause)) {
      throw new ChatStreamAbortedError()
    }
    throw parseCallableError(cause)
  }
}
